<template>
    <div class="date-cell" :class="field.alignment" @dblclick="enableEdit">
        <template v-if="editing">
            <a-date-picker
                v-if="!includeTime"
                :value="getDateValue()"
                :format="format"
                @change="onDateChange"
                @ok="disableEdit"
                ref="datePicker"
            />
            <a-date-picker
                v-else
                :value="getDateTimeValue()"
                :show-time="{ format: timeFormat }"
                :format="`${format} ${timeFormat}`"
                @change="onDateTimeChange"
                @ok="disableEdit"
                ref="dateTimePicker"
            />
        </template>
        <template v-else>
            {{ formattedValue }}
        </template>
    </div>
</template>

<script>
import Vue from "vue";
import moment from "moment-timezone";

export default Vue.extend({
    name: "DateCell",
    props: {
        record: {
            type: Object,
            required: true,
        },
        field: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            editing: false,
            value: this.record.data[this.field._id] || "",
        };
    },
    computed: {
        formattedValue() {
            if (!this.value) return "";
            return this.includeTime
                ? moment.tz(this.value, this.timezone).format(`${this.format} ${this.timeFormat}`)
                : moment.tz(this.value, this.timezone).format(this.format);
        },
        timezone() {
            return this.field.options?.datetime?.timezone || "UTC";
        },
        format() {
            return this.field.options?.datetime?.format || "YYYY-MM-DD";
        },
        includeTime() {
            return this.field.options?.datetime?.includeTime || false;
        },
        timeFormat() {
            return this.field.options?.datetime?.timeFormat || "HH:mm";
        },
    },
    methods: {
        enableEdit() {
            this.editing = true;
            this.$nextTick(() => {
                if (this.includeTime) {
                    this.$refs.dateTimePicker.focus();
                } else {
                    this.$refs.datePicker.focus();
                }
            });
        },
        async disableEdit() {
            this.editing = false;
            try {
                const isChanged = this.record.data[this.field._id] !== this.value;
                if (isChanged) {
                    await this.$emit("update-record", {
                        recordId: this.record._id,
                        fieldId: this.field._id,
                        value: this.value,
                    });
                }
            } catch (error) {
                console.error(this.$t("tables.recordUpdatingError"), error);
                this.$message.error(this.$t("tables.recordUpdatingError"));
            }
        },
        onDateChange(date) {
            this.value = date.format(this.format);
        },
        onDateTimeChange(date) {
            this.value = date.format(`${this.format} ${this.timeFormat}`);
        },

        getDateValue() {
            return this.value ? moment(this.value, this.format) : null;
        },

        getDateTimeValue() {
            return this.value ? moment(this.value, `${this.format} ${this.timeFormat}`) : null;
        },
    },
});
</script>

<style scoped lang="scss">
.date-cell {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;

    &.left {
        justify-content: flex-start;
    }

    &.center {
        justify-content: center;
    }

    &.right {
        justify-content: flex-end;
    }

    .ant-calendar-picker {
        min-width: 195px;
        width: 100%;
        border: none;
        border-radius: unset;
        height: max-content;

        input {
            padding: 0px 3px;
            height: 36px;
            border: none;
        }
    }
}
</style>
